import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import {
  isTripSummaryIcon,
  isTripSummaryText,
} from '@/components/organisms/trip-summary/trip-summary.adapter';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './trip-summary.module.scss';

export interface TripSummaryProps {
  /** The items to show */
  tripSummaryItems?: Content.Properties.TripSummaryItem[];
}

const ICON_WIDTH = 24;
const MIN_DIVIDER_WIDTH = 20;
const GAP_WIDTH = 8;
const PADDING_WIDTH = 24;
const OFFSET = 54;

const TripSummary = ({ tripSummaryItems }: TripSummaryProps) => {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<
    Content.Properties.TripSummaryItem[]
  >([]);
  const [remainingCount, setRemainingCount] = useState<number>(0);

  const hasDivider = (itemIndex: number): boolean => {
    return itemIndex < visibleItems.length - 1 || remainingCount > 0;
  };

  const getTextWidth = (
    temporarySpan: HTMLSpanElement,
    text: string,
    isBold: boolean = false,
  ): number => {
    if (!containerRef.current) return 0;

    const container = containerRef.current;

    temporarySpan.style.visibility = 'hidden';
    temporarySpan.style.fontSize = '12px';
    temporarySpan.style.fontWeight = isBold ? 'bold' : 'normal';
    container.appendChild(temporarySpan);
    temporarySpan.innerText = text;
    const textWidth = temporarySpan.offsetWidth;

    container.removeChild(temporarySpan);

    return textWidth;
  };

  const calculateVisibleItems = () => {
    if (!containerRef.current || !tripSummaryItems) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;

    // Always start with an icon and gap
    let currentWidth = ICON_WIDTH + GAP_WIDTH;
    let overflowCount = 0;
    const fitItems: Content.Properties.TripSummaryItem[] = [];
    const temporarySpan = document.createElement('span');

    for (const item of tripSummaryItems) {
      let sectionWidth = 0;

      const isLastItem = item === tripSummaryItems[tripSummaryItems.length - 1];
      const distanceToNextItem = isLastItem
        ? GAP_WIDTH
        : GAP_WIDTH * 2 + MIN_DIVIDER_WIDTH;

      if (isTripSummaryIcon(item)) {
        sectionWidth = ICON_WIDTH + distanceToNextItem;
      } else if (isTripSummaryText(item)) {
        const primaryTextWidth = getTextWidth(
          temporarySpan,
          item.boldText,
          true,
        );
        const secondaryTextWidth = getTextWidth(temporarySpan, item.text);

        // Max width between primary and secondary text, since they are stacked
        sectionWidth =
          Math.max(primaryTextWidth, secondaryTextWidth) + distanceToNextItem;
      }

      // Check if adding this item would overflow the container
      if (
        currentWidth + sectionWidth + PADDING_WIDTH + OFFSET >
        containerWidth
      ) {
        overflowCount = tripSummaryItems.length - fitItems.length;
        break;
      } else {
        fitItems.push(item);
        currentWidth += sectionWidth;
      }
    }

    setVisibleItems(fitItems);
    setRemainingCount(overflowCount);
  };

  useEffect(() => {
    calculateVisibleItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={containerRef} className={styles.component}>
      <Icon className={styles.icon} name="PlaneTakeOff" size={24} />

      {visibleItems.map((item, itemIndex) => (
        <React.Fragment key={itemIndex}>
          {isTripSummaryIcon(item) && item.icon && (
            <Icon className={styles.icon} name={item.icon} size={24} />
          )}

          {isTripSummaryText(item) && (
            <div className={styles.info}>
              <Typography
                className={styles.primaryText}
                tagStyle="bodySmallBold"
              >
                {item.boldText}
              </Typography>

              <Typography className={styles.secondaryText} tagStyle="bodySmall">
                {item.text}
              </Typography>
            </div>
          )}

          {hasDivider(itemIndex) && <div className={styles.divider} />}
        </React.Fragment>
      ))}

      {remainingCount > 0 ? (
        <Typography className={styles.more} tagStyle="bodySmallBold">
          {`...+${remainingCount} ${t('items.common.words.more')}`}
        </Typography>
      ) : (
        <Icon className={styles.icon} name="PlaneLanding" size={24} />
      )}
    </div>
  );
};

TripSummary.displayName = 'TripSummary';

export default TripSummary;
